/**
 * Settings → AI: the providers you added (keys stay on this device), which of
 * your providers does which task, and what it cost this month. There is no
 * shared/server-paid model -- every user brings their own provider.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Plus, Trash2 } from 'lucide-react';
import { listModels, PRESETS, type AiMode } from '@nexus/ai';
import { isTauri } from '@nexus/store/tauri';
import { useAiChat } from './chat';
import {
  AI_TASKS,
  destination,
  directSpend,
  getKey,
  needsKey,
  refreshKeyed,
  routeFor,
  setKey,
  deleteKey,
  useAiProviders,
  type ProviderSetting,
} from './providers';

const field = 'rounded-lg border border-border bg-bg px-2 py-1 text-sm';
const button =
  'flex items-center gap-1 rounded-lg border border-border px-2 py-1 text-sm hover:bg-bg-hover disabled:opacity-50';

export function AiSettings() {
  const { t, i18n } = useTranslation();
  const { providers, add } = useAiProviders();
  const [preset, setPreset] = useState(PRESETS[0]!.id);
  useAiChat((s) => s.usage); // re-render after each answer: the estimate grows
  const usd = (n: number) =>
    new Intl.NumberFormat(i18n.language, { style: 'currency', currency: 'USD' }).format(n);

  useEffect(refreshKeyed, []);

  return (
    <div className="flex flex-col gap-6 text-sm">
      <section aria-labelledby="ai-providers" className="flex flex-col gap-3">
        <h4 id="ai-providers" className="font-medium">
          {t('aiSettings.providers')}
        </h4>
        <p className="text-xs text-fg-muted">
          {t(isTauri() ? 'aiSettings.keysDesktop' : 'aiSettings.keysBrowser')}
        </p>
        {providers.map((p) => (
          <ProviderCard key={p.id} provider={p} />
        ))}
        <div className="flex flex-wrap items-center gap-2">
          <select
            aria-label={t('aiSettings.kind')}
            className={field}
            value={preset}
            onChange={(e) => setPreset(e.target.value)}
          >
            {PRESETS.map((p) => (
              <option key={p.id} value={p.id}>
                {t(`aiSettings.presets.${p.id}`)}
              </option>
            ))}
          </select>
          <button type="button" className={button} onClick={() => add(preset)}>
            <Plus className="size-3.5" />
            {t('aiSettings.add')}
          </button>
        </div>
      </section>

      <section aria-labelledby="ai-tasks" className="flex flex-col gap-2">
        <h4 id="ai-tasks" className="font-medium">
          {t('aiSettings.tasks')}
        </h4>
        <p className="text-xs text-fg-muted">{t('aiSettings.tasksHint')}</p>
        <div className="grid grid-cols-[9rem_1fr] items-center gap-x-4 gap-y-2">
          {AI_TASKS.map((task) => (
            <TaskRoute key={task} task={task} />
          ))}
        </div>
      </section>

      <section aria-labelledby="ai-usage" className="flex flex-col gap-1">
        <h4 id="ai-usage" className="font-medium">
          {t('ai.usage')}
        </h4>
        <p className="text-fg-muted">
          {t('aiSettings.directUsage')}: {usd(directSpend())}
        </p>
        <p className="text-xs text-fg-muted">{t('ai.privacyHelp')}</p>
      </section>
    </div>
  );
}

/** One task: which model does it. */
function TaskRoute({ task }: { task: AiMode }) {
  const { t } = useTranslation();
  const state = useAiProviders();
  const chosen = state.routes[task];
  const current = routeFor(task, state);
  const id = `ai-route-${task}`;
  const value = chosen ? `${chosen.provider}::${chosen.model}` : '';
  const provider = state.providers.find((p) => p.id === current?.provider);
  const where = provider ? destination(provider) : null;

  return (
    <>
      <label htmlFor={id}>{t(`aiSettings.taskNames.${task}`)}</label>
      <div className="flex min-w-0 flex-col gap-0.5">
        <select
          id={id}
          className={field}
          value={value}
          onChange={(e) => {
            const [p, ...m] = e.target.value.split('::');
            state.setRoute(task, p ? { provider: p, model: m.join('::') } : null);
          }}
        >
          <option value="">{t('aiSettings.default')}</option>
          {state.providers.map((p) => (
            <optgroup key={p.id} label={p.name}>
              {p.models.map((m) => (
                <option key={m} value={`${p.id}::${m}`}>
                  {m}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
        <span className="text-xs text-fg-muted">
          {!current
            ? t('aiSettings.noModel')
            : where?.local
              ? t('aiSettings.local')
              : t('aiSettings.sentTo', { host: where?.host ?? '' })}
          {task === 'plugin' && provider && !provider.tools && ` ${t('aiSettings.needsTools')}`}
        </span>
      </div>
    </>
  );
}

function ProviderCard({ provider: p }: { provider: ProviderSetting }) {
  const { t } = useTranslation();
  const { update, remove, keyed } = useAiProviders();
  const [key, setKeyDraft] = useState('');
  const [models, setModels] = useState(p.models.join(', '));
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const where = destination(p);
  const prefix = `ai-${p.id}`;

  const saveModels = (text: string) =>
    update(p.id, {
      models: [
        ...new Set(
          text
            .split(',')
            .map((m) => m.trim())
            .filter(Boolean),
        ),
      ],
    });

  return (
    <fieldset className="flex flex-col gap-2 rounded-xl border border-border p-3">
      <legend className="px-1 text-xs text-fg-muted">{t(`aiSettings.presets.${p.preset}`)}</legend>
      <div className="grid grid-cols-[7rem_1fr] items-center gap-x-3 gap-y-2">
        <label htmlFor={`${prefix}-name`}>{t('aiSettings.name')}</label>
        <input
          id={`${prefix}-name`}
          className={field}
          value={p.name}
          onChange={(e) => update(p.id, { name: e.target.value })}
        />

        {p.kind === 'openai' && (
          <>
            <label htmlFor={`${prefix}-url`}>{t('aiSettings.address')}</label>
            <input
              id={`${prefix}-url`}
              className={field}
              value={p.baseUrl ?? ''}
              placeholder="https://…/v1"
              spellCheck={false}
              onChange={(e) => update(p.id, { baseUrl: e.target.value.trim() })}
            />
          </>
        )}

        {(needsKey(p) || p.preset === 'custom') && (
          <>
            <label htmlFor={`${prefix}-key`}>{t('aiSettings.key')}</label>
            <div className="flex gap-2">
              <input
                id={`${prefix}-key`}
                type="password"
                autoComplete="off"
                className={`${field} min-w-0 flex-1`}
                value={key}
                placeholder={
                  keyed[p.id] ? t('aiSettings.keySaved') : t('aiSettings.keyPlaceholder')
                }
                onChange={(e) => setKeyDraft(e.target.value)}
              />
              <button
                type="button"
                className={button}
                disabled={!key.trim()}
                onClick={() =>
                  void setKey(p.id, key).then(
                    () => {
                      setKeyDraft('');
                      setNote(t('aiSettings.keyStored'));
                    },
                    (e: unknown) => setNote(String(e)),
                  )
                }
              >
                {t('aiSettings.save')}
              </button>
              {keyed[p.id] && (
                <button
                  type="button"
                  className={button}
                  aria-label={t('aiSettings.forgetKey')}
                  title={t('aiSettings.forgetKey')}
                  onClick={() => void deleteKey(p.id)}
                >
                  <Trash2 className="size-3.5" />
                </button>
              )}
            </div>
          </>
        )}

        <label htmlFor={`${prefix}-models`}>{t('aiSettings.models')}</label>
        <div className="flex gap-2">
          <input
            id={`${prefix}-models`}
            className={`${field} min-w-0 flex-1`}
            value={models}
            placeholder={t('aiSettings.modelsPlaceholder')}
            spellCheck={false}
            onChange={(e) => setModels(e.target.value)}
            onBlur={() => saveModels(models)}
          />
          {p.kind === 'openai' && (
            <button
              type="button"
              className={button}
              disabled={busy || !p.baseUrl}
              title={t('aiSettings.fetchModels')}
              onClick={() => {
                setBusy(true);
                setNote(null);
                void (async () => {
                  const apiKey = (await getKey(p.id)) ?? undefined;
                  const doFetch = isTauri()
                    ? ((await import('@tauri-apps/plugin-http')).fetch as unknown as typeof fetch)
                    : fetch;
                  const found = await listModels(p.baseUrl!, apiKey, doFetch);
                  if (found.length) {
                    setModels(found.join(', '));
                    saveModels(found.join(', '));
                    setNote(t('aiSettings.modelsFound', { count: found.length }));
                  } else setNote(t('aiSettings.modelsNotFound'));
                })().finally(() => setBusy(false));
              }}
            >
              <Download className="size-3.5" />
              {t('aiSettings.fetch')}
            </button>
          )}
        </div>

        <span />
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={p.tools}
            onChange={(e) => update(p.id, { tools: e.target.checked })}
          />
          {t('aiSettings.tools')}
        </label>

        {!where.local && p.kind === 'openai' && (
          <>
            <span>{t('aiSettings.price')}</span>
            <div className="flex items-center gap-2 text-xs text-fg-muted">
              <input
                aria-label={t('aiSettings.priceIn')}
                type="number"
                min={0}
                step="0.01"
                className={`${field} w-20`}
                value={p.price?.[0] ?? ''}
                onChange={(e) =>
                  update(p.id, { price: [Number(e.target.value) || 0, p.price?.[1] ?? 0] })
                }
              />
              {t('aiSettings.in')}
              <input
                aria-label={t('aiSettings.priceOut')}
                type="number"
                min={0}
                step="0.01"
                className={`${field} w-20`}
                value={p.price?.[1] ?? ''}
                onChange={(e) =>
                  update(p.id, { price: [p.price?.[0] ?? 0, Number(e.target.value) || 0] })
                }
              />
              {t('aiSettings.out')}
            </div>
          </>
        )}
      </div>
      <p className="text-xs text-fg-muted">
        {where.local
          ? t('aiSettings.localHint')
          : t('aiSettings.cloudHint', { host: where.host || '…' })}
        {p.preset === 'ollama' && !isTauri() && ` ${t('aiSettings.ollamaBrowser')}`}
      </p>
      {note && (
        <p role="status" className="text-xs text-fg-muted">
          {note}
        </p>
      )}
      <button
        type="button"
        className="self-end text-xs text-danger hover:underline"
        onClick={() => remove(p.id)}
      >
        {t('aiSettings.remove')}
      </button>
    </fieldset>
  );
}
